import { Link } from 'react-router-dom';
import { useBookmarks, useRemoveBookmark } from '@/hooks/useBookmarks';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';

export function Bookmarks() {
  useDocumentTitle('Bookmarks');
  const bookmarks = useBookmarks();
  const remove = useRemoveBookmark();

  if (bookmarks.isError) {
    return (
      <EmptyState
        tone="error"
        title="We couldn't load your bookmarks."
        description={(bookmarks.error as Error | null)?.message ?? undefined}
        onRetry={() => bookmarks.refetch()}
      />
    );
  }

  return (
    <section className="container-page pt-12 sm:pt-16 pb-16">
      <header className="mb-10">
        <p className="text-xs uppercase tracking-[0.22em] text-accent-600 dark:text-accent-400 mb-3">
          Saved
        </p>
        <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50">
          Bookmarks
        </h1>
      </header>

      {bookmarks.isLoading ? (
        <BookmarksSkeleton />
      ) : !bookmarks.data?.length ? (
        <EmptyState
          title="No bookmarks yet."
          description="Tap the bookmark icon on any ayah to save it. They'll show up here in the order you added them."
          action={
            <Link to="/surah">
              <Button variant="secondary">Open the Quran</Button>
            </Link>
          }
        />
      ) : (
        <ul className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
          {bookmarks.data.map((b) => (
            <li
              key={b.id}
              className="flex items-center gap-4 py-4 sm:py-5 group"
            >
              <Link
                to={`/surah/${b.surah_id}/ayah/${b.ayah_number}`}
                className="flex-1 -mx-3 px-3 py-2 rounded-md
                           transition-colors hover:bg-ink-100/60 dark:hover:bg-ink-900/40"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-serif text-base text-ink-800 dark:text-ink-100 num-tight">
                    Surah {b.surah_id}, Ayah {b.ayah_number}
                  </span>
                  <span className="text-xs text-ink-400 dark:text-ink-500">
                    {new Date(b.created_at).toLocaleDateString()}
                  </span>
                </div>
                {b.label && (
                  <p className="text-sm text-ink-500 dark:text-ink-400 mt-1">
                    {b.label}
                  </p>
                )}
              </Link>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => remove.mutate(b.id)}
                disabled={remove.isPending}
                aria-label={`Remove bookmark for surah ${b.surah_id} ayah ${b.ayah_number}`}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BookmarksSkeleton() {
  return (
    <ul className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="py-5">
          <Skeleton className="h-4 w-40 mb-2" />
          <Skeleton className="h-3 w-56" />
        </li>
      ))}
    </ul>
  );
}
